import React, { createContext, useEffect, useState } from "react";
import BannerManager from "./components/BannerManager";
import AdminCourseEditor from "./components/AdminCourseEditor";
import AdminLiveClassEditor from "./components/AdminLiveClassEditor";
import AdminVideoCornerEditor from "./components/AdminVideoCornerEditor";
import AdminFacultyEditor from "./components/AdminFacultyEditor";
import { useGetLandingDetailsQuery, useUpdateLandingDetailsMutation } from "../../redux/landing/landing.api";
import AchieversEditor from "./components/AchieverEditor";

interface LandingEditsProps {
  title?: string;
}

type LandingDetails = {
  banner: string[];

  courses: {
    id: string;
    type: "main" | "optional";
    image: string;
    title: string;
    discountPercent: number;
    price: number;
    originalPrice: number;
    buttonText: string;
    targetExam?: string;
    duration?: string;
    classStartDate?: string;
    fees?: string;
    installments?: string;
    keyBenefits?: string[];
    videoLink?: string;
  }[];

  live_class: {
    id: string;
    tabId: string;
    tag: string;
    subtitle: string;
    title: string;
    status: "LIVE NOW" | "SESSION ENDED" | "COMING SOON";
    time: string;
    actionText: string;
    instructor: string;
    type: string;
    image: string;
  }[];

  video_corner?: {
    id: string;
    link: string;
  }[];

  faculty?: {
    id: string;
    name: string;
    title: string;
    image: string;
  }[];

  achievers?: {
    id: string;
    image: string;
    name: string;
    homeDistrict: string;
    batch: string;
    selectedExam: string;
    selectedPost: string;
    feedback: string;
    podcastLink: string;
  }[];
};
interface LandingDetailsContextType {
  landingDetails: LandingDetails;
  setLandingDetails: React.Dispatch<React.SetStateAction<LandingDetails>>;
}

export const landingPageContext = createContext<LandingDetailsContextType | undefined>(undefined);

const LandingEdits: React.FC<LandingEditsProps> = () => {
  const { data, isLoading, isError } = useGetLandingDetailsQuery();
  const [updateLandingDetails] = useUpdateLandingDetailsMutation();
  const [landingDetails, setLandingDetails] = useState<LandingDetails>({} as LandingDetails);

  useEffect(() => {
    if (data) {
      console.log("data", data);
      setLandingDetails(data);
    }
  }, [data]);

  const handleSave = async () => {
    if (!landingDetails) return;
    try {
      await updateLandingDetails(landingDetails).unwrap();
      alert("Landing page changes saved successfully!");
    } catch (error) {
      console.error("Failed to save landing page changes:", error);
    }
  };

  if (isLoading || !landingDetails) {
    return <div className="p-6">Loading landing data...</div>;
  }

  if (isError) {
    return <div className="p-6 text-red-600">Failed to load landing data.</div>;
  }
  return (
    <landingPageContext.Provider value={{ landingDetails, setLandingDetails }}>
      {Object.values(landingDetails).length == 0 ? (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-lg text-gray-500 animate-pulse">Loading landing data...</div>
        </div>
      ) : (
        <>
          <div className="fixed top-4 right-4 z-50">
            <button
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded shadow-lg"
            >
              💾 Save All Changes
            </button>
          </div>

          <div className="min-h-screen bg-white text-gray-800 p-8 flex flex-col space-y-12 w-full">
            <h1 className="text-3xl font-bold mb-4">Landing page edits</h1>
            <BannerManager />
            <AdminLiveClassEditor />
            <AdminVideoCornerEditor />
            <AdminFacultyEditor />
            <AchieversEditor/>
          </div>
        </>
      )}
    </landingPageContext.Provider>
  );
};

export default LandingEdits;
